<script setup lang="ts">

import {router, usePage} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {useUserAvatar} from "@/composables/avatar";
import {ExtendedProject, User} from "@/types";
import SendcryptLogo from "@/components/SendcryptLogo.vue";
import {useFlashData} from "@/composables/flash";
import {computed, mergeProps} from "vue";
import SearchForm from "@/components/forms/SearchForm.vue";
import {useDisplay} from "vuetify";
import InfoAlert from "@/components/alerts/InfoAlert.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";

interface Props {
    user: User
    title: string
    project?: ExtendedProject
}

const props = defineProps<Props>()
const emit = defineEmits(['showMenu', 'showUserMenu', 'showSearch'])
const page = usePage()

const { smAndDown, mdAndUp } = useDisplay()

const unreadNotificationsCount = computed(() => page.props.unreadNotificationsCount as number)
</script>

<template>
    <v-app-bar
        color="grey-lighten-4"
        class="border-b"
        :flat="true"
        extension-height="62"
    >
        <template
            v-if="useFlashData && useFlashData.message"
            #extension
        >
            <error-alert
                v-if="useFlashData.message_type === 'error'"
                rounded="0"
            >
                {{ useFlashData.message }}
            </error-alert>
            <info-alert
                v-else
                rounded="0"
            >
                {{ useFlashData.message }} {{ }}
            </info-alert>
        </template>
        <v-btn
            class="border mx-5"
            rounded="lg"
            icon="mdi-menu"
            size="x-small"
            @click.stop="emit('showMenu')"
        >
            <v-icon
                size="20"
                color="grey-darken-3"
            >
                mdi-menu
            </v-icon>
        </v-btn>
        <sendcrypt-logo />
        <div
            v-if="!route().current('search') && mdAndUp"
            class="font-weight-medium ml-5 text-subtitle-2 mb-1"
        >
            {{ props.title }}
            <v-chip
                v-if="props.project && mdAndUp"
                variant="outlined"
                size="x-small"
                class="text-caption font-weight-bold px-2 ml-1"
                color="grey-lighten-1"
            >
                <span class="text-grey-darken-1">{{ props.project.is_public ? 'Public' : 'Private' }}</span>
            </v-chip>
        </div>
        <v-spacer v-if="smAndDown || !route().current('search')" />
        <search-form
            @show-search-dialog="$emit('showSearch')"
        />
        <v-divider
            class="mx-4"
            style="height: 20px;margin-bottom: auto;margin-top: auto;"
            :inset="true"
            :vertical="true"
        />
        <v-tooltip
            location="bottom"
            :offset="5"
            content-class="rounded-lg"
        >
            <template #activator="{ props: tooltip }">
                <success-btn
                    class="border mr-2"
                    v-bind="tooltip"
                    rounded="lg"
                    size="small"
                    href="https://sendcrypt.sib.swiss/download"
                    style="height: 32px;"
                >
                    Download
                </success-btn>
            </template>
            Download the latest version of SendCrypt Desktop Application
        </v-tooltip>
        <v-menu
            offset="5"
        >
            <template #activator="{ props: bind }">
                <v-tooltip
                    location="bottom"
                    :offset="5"
                    content-class="rounded-lg"
                >
                    <template #activator="{ props: tooltip }">
                        <v-btn
                            class="border"
                            v-bind="mergeProps(bind, tooltip)"
                            icon="mdi-plus"
                            rounded="lg"
                            size="x-small"
                            style="width: 54px;"
                        >
                            <template #default>
                                <v-icon
                                    size="20"
                                    color="grey-darken-3"
                                >
                                    mdi-plus
                                </v-icon>
                                <v-icon
                                    class="pl-2"
                                    color="grey-darken-3"
                                >
                                    mdi-chevron-down
                                </v-icon>
                            </template>
                        </v-btn>
                    </template>
                    Create new...
                </v-tooltip>
            </template>
            <v-list
                density="compact"
                class="pa-2 rounded-lg border shadow"
                width="200"
                rounded="lg"
                elevation="0"
            >
                <v-list-item
                    prepend-icon="mdi-folder-outline"
                    density="compact"
                    rounded="lg"
                    :link="true"
                    :slim="true"
                    @click="router.visit(route('projects.create'))"
                >
                    <v-list-item-title class="ml-2 font-weight-regular text-body-2">
                        New project
                    </v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <v-tooltip
            location="bottom"
            :offset="5"
            content-class="rounded-lg"
        >
            <template #activator="{ props: tooltip }">
                <v-btn
                    v-bind="tooltip"
                    class="border mx-2"
                    icon="mdi-inbox-outline"
                    size="x-small"
                    rounded="lg"
                    @click="router.visit(route('notifications.index'))"
                >
                    <v-badge
                        color="error"
                        :dot="true"
                        :floating="true"
                        :model-value="unreadNotificationsCount > 0"
                    >
                        <v-icon
                            size="20"
                            color="grey-darken-3"
                        >
                            mdi-inbox-outline
                        </v-icon>
                    </v-badge>
                </v-btn>
            </template>
            You have {{ unreadNotificationsCount > 0 ? unreadNotificationsCount : 'no' }} unread notifications
        </v-tooltip>
        <v-avatar
            class="border-sm cursor-pointer mr-5"
            size="36"
            @click="emit('showUserMenu')"
        >
            <v-img
                :src="useUserAvatar(props.user.name)"
                contain
            />
        </v-avatar>
    </v-app-bar>
</template>

<style scoped>
.shadow {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1) !important;
}
</style>
